import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import type { PostHog } from 'posthog-js';
import { GetterService } from 'src/app/service/getter-service.service';
import { ApiService } from 'src/app/service/api.service';
import { AnalyticsConsentMode } from 'src/Model/WebCustomer';

/**
 * Two modes, picked per brand by `TemplateData.Analytics.ConsentMode`:
 *
 * - `cookieless` (default): PostHog starts as soon as analytics is enabled,
 *   with `persistence: 'memory'` — nothing is written to or read from the
 *   visitor's device, so PECR reg. 6 (the "cookie law") is not engaged and no
 *   consent banner is shown. Session replay and autocapture stay off and
 *   anonymous visitors get no person profile ("how the app is used", not
 *   "who"). The visitor can still opt out from the privacy page; only that
 *   refusal is stored (brand-scoped `analyticsOptOut`), which is a
 *   strictly-necessary preference. Anonymous `distinct_id`s live for one page
 *   load, so anonymous cross-visit retention is not available in this mode;
 *   identified customers (`identifyUser`, `#ph_distinct_id`) are unaffected.
 *
 * - `consent`: the previous behaviour — the cookie banner gates everything,
 *   and on acceptance PostHog runs with its default cookie + localStorage
 *   persistence, session replay and the brand's AutoCapture setting.
 *
 * posthog-js (~170 KB) is only downloaded once tracking is actually allowed —
 * `import('posthog-js')` puts it in its own lazy chunk. Calls made while that
 * chunk is loading are queued on `ready` and run once it lands.
 */
@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  private static readonly CONSENT_KEY = 'analyticsConsent';
  private static readonly OPT_OUT_KEY = 'analyticsOptOut';

  private consentGiven = false;
  private posthogKey: string;
  private posthogHost: string;
  private enabled = false;
  private autoCapture: boolean;
  private mode: AnalyticsConsentMode = 'cookieless';
  /** resolves with the initialised client; null until initPosthog() starts */
  private ready: Promise<PostHog> | null = null;
  private client: PostHog | null = null;
  private routerHooked = false;

  constructor(
    private router: Router,
    private local: GetterService,
    private apiService: ApiService
  ) {
    this.getWebCustomer();
  }

  getWebCustomer() {
    this.apiService.getWebcustomer$.subscribe((res) => {
      if (!res) {
        return;
      }

      const analytics = res.TemplateData?.Analytics;
      this.enabled = !!analytics?.Enabled;
      this.posthogKey = analytics?.PostHogKey;
      this.posthogHost = analytics?.PostHogHost;
      this.autoCapture = analytics?.AutoCapture;
      this.mode = analytics?.ConsentMode === 'consent' ? 'consent' : 'cookieless';

      if (!this.enabled || !this.posthogKey || !this.posthogHost) return;

      if (this.mode === 'consent') {
        this.loadConsent();
      } else if (!this.isOptedOut()) {
        this.initPosthog();
      }
    });
  }

  // ----- state for the banner / privacy page -------------------------------

  /** Analytics enabled for this brand (any mode). */
  isEnabled(): boolean {
    return this.enabled;
  }

  getMode(): AnalyticsConsentMode {
    return this.mode;
  }

  /** Banner is only needed in `consent` mode. */
  needsConsentBanner(): boolean {
    return this.enabled && this.mode === 'consent';
  }

  /** True while events are (or will be, once the chunk lands) being sent. */
  isTracking(): boolean {
    if (!this.enabled) return false;
    return this.mode === 'consent' ? this.consentGiven : !this.isOptedOut();
  }

  isOptedOut(): boolean {
    return this.local.get(PosthogService.OPT_OUT_KEY) === 'true';
  }

  // ----- cookieless mode: opt-out ------------------------------------------

  /** Visitor declines statistics (privacy page). Stops capture immediately. */
  optOut() {
    this.local.set(PosthogService.OPT_OUT_KEY, 'true');
    this.ready?.then((posthog) => posthog.opt_out_capturing());
  }

  /** Visitor re-enables statistics. */
  optIn() {
    this.local.remove(PosthogService.OPT_OUT_KEY);
    if (this.mode !== 'cookieless') return;
    if (this.ready) {
      this.ready.then((posthog) => posthog.opt_in_capturing());
    } else {
      this.initPosthog();
    }
  }

  // ----- consent mode: banner -----------------------------------------------

  private loadConsent() {
    const storedConsent = this.local.get(PosthogService.CONSENT_KEY);
    if (storedConsent === 'true') {
      this.consentGiven = true;
      this.initPosthog();
    }
  }

  giveConsent() {
    this.consentGiven = true;
    this.saveConsent(true);
    if (this.ready) {
      this.ready.then((posthog) => posthog.opt_in_capturing());
    } else {
      this.initPosthog();
    }
  }

  private saveConsent(value: boolean) {
    this.local.set(PosthogService.CONSENT_KEY, value.toString());
  }

  revokeConsent() {
    this.consentGiven = false;
    this.saveConsent(false);
    this.ready?.then((posthog) => posthog.opt_out_capturing());
  }

  // ----- client -------------------------------------------------------------

  getDistinctIdFromHash(): string | null {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.substring(1)
      : window.location.hash;

    const params = new URLSearchParams(hash);
    return params.get('ph_distinct_id');
  }

  initPosthog() {
    if (!this.isTracking() || !this.posthogKey || !this.posthogHost) return;
    // the config subscription, the banner and the privacy page can all get here
    if (this.ready) return;

    if (!this.routerHooked) {
      this.routerHooked = true;
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd && this.isTracking()) {
          this.client?.capture('$pageview', { path: event.urlAfterRedirects });
        }
      });
    }

    const distinctId = this.getDistinctIdFromHash();
    const cookieless = this.mode === 'cookieless';

    this.ready = import('posthog-js').then(({ default: posthog }) => {
      posthog.init(this.posthogKey, {
        api_host: this.posthogHost,
        // NavigationEnd above sends the pageviews (also covers the first one)
        capture_pageview: false,
        bootstrap: distinctId
          ? { distinctID: distinctId, isIdentifiedID: true }
          : undefined,
        ...(cookieless
          ? {
              // nothing stored on or read from the device
              persistence: 'memory' as const,
              autocapture: false,
              disable_session_recording: true,
              // profiles only for customers we identify (login / #ph_distinct_id)
              person_profiles: 'identified_only' as const,
              // don't keep the visitor's IP on events
              ip: false,
            }
          : {
              autocapture: this.autoCapture,
              disable_session_recording: false,
              person_profiles: 'always' as const,
            }),
      });

      if (distinctId) {
        const currentId = posthog.get_distinct_id();
        if (currentId !== distinctId) {
          posthog.alias(distinctId);
          posthog.identify(distinctId);
        }
      }

      this.client = posthog;
      // the first NavigationEnd may already have passed while the chunk loaded
      posthog.capture('$pageview', { path: this.router.url });
      return posthog;
    });
  }

  captureEvent(eventName: string, properties?: Record<string, any>) {
    if (this.isTracking() && this.ready)
      this.ready.then((posthog) => posthog.capture(eventName, properties));
  }

  identifyUser(userId: string, properties?: Record<string, any>) {
    const distinctId = this.getDistinctIdFromHash();

    if (this.isTracking() && !distinctId && this.ready) {
      this.ready.then((posthog) => {
        posthog.identify(userId);
        if (properties) {
          posthog.people.set(properties);
        }
      });
    }
  }
}
